// Mesquite Mafia: Compound Operations & Fortifications
import { state, log, updateCurrencies, updateHUDDirective, addXP } from '../core/state.js';
import { playSFX } from '../core/audio.js';
import { triggerGunAnimation, playActionSequence } from '../core/kinematics.js';
import { updateMissionProgress } from './missions.js';
import { syncStateToD1, callServerAction } from '../services/api.js';
import { openVenmoStoreModal } from '../services/store.js';
import { update4XTelemetry, selectSector, sectorData } from './world.js';

export function triggerCompoundAction(action) {
      if (action === 'wash') {
        const washCost = 100;
        if (state.cash < washCost) {
          playActionSequence({
            title: "INSUFFICIENT DIRTY CASH",
            icon: "❌",
            detail: `Need at least $${washCost} dirty cash to run a laundry cycle in Room 114!`,
            rewardText: "Scavenge scrap or complete starter missions to get cash",
            shots: 0
          });
          return;
        }

        state.cash = state.cash - washCost + 140;
        state.cleanCapital = (state.cleanCapital || 0) + 100;
        if (state.policeHeat > 0 && Math.random() < 0.5) state.policeHeat--;

        playActionSequence({
          title: "BACK-OFFICE WASH",
          icon: "💵",
          detail: "Pressing dirty paper through commercial dryers in Room 114...",
          rewardText: "+$140 Clean Cash • +$100 Clean Capital • -Heat",
          shots: 1,
          onComplete: () => { SFX.cash(); log("[COMPOUND]: $140 clean bankroll ready."); }
        });
        updateMissionProgress('wash', 1);
      } else if (action === 'cook') {
        const productYield = 50;
        state.product += productYield;
        playActionSequence({
          title: "THE COOKSHACK",
          icon: "📦",
          detail: "Weighing, sealing, and packing product bricks...",
          rewardText: `+${productYield} Distro Units Ready For Transit`,
          shots: 1,
          onComplete: () => { SFX.cash(); log(`[COMPOUND]: Packaged ${productYield} units.`); }
        });
        updateMissionProgress('cook', 1);
      } else if (action === 'train') {
        playActionSequence({
          title: "PAVED LOT TARGET DRILL",
          icon: "🎯",
          detail: "Fat Twin & Skinny Black putting rounds downrange!",
          rewardText: "+40 Combat Proficiency XP",
          shots: 4,
          onComplete: () => log("[COMPOUND]: Tactical drills executed.")
        });
        updateMissionProgress('train', 1);
      } else if (action === 'scanner') {
        SFX.radio();
        const dispatches = [
          "Mesquite PD: Code 3 patrol unit near US-80 frontage corridor.",
          "Scanner: Flatbed convoy moving scrap near Big Town Blvd...",
          "Municipal Intel: Vance authorizing routine patrol sweeps on I-635.",
          "Scanner Intercept: Rodeo pen guards rotating shift at 22:00."
        ];
        const randomDispatch = dispatches[Math.floor(Math.random() * dispatches.length)];
        const scEl = document.getElementById('scanner-text');
        if (scEl) scEl.innerText = randomDispatch;
        playActionSequence({
          title: "POLICE SCANNER TAP",
          icon: "📡",
          detail: randomDispatch,
          rewardText: "Telemetry Synchronized • +20 XP",
          shots: 0,
          onComplete: () => log("[SCANNER]: " + randomDispatch)
        });
        updateMissionProgress('scanner', 1);
      }
      
      const xpGain = action === 'train' ? 40 : action === 'wash' ? 25 : action === 'cook' ? 30 : 20;
      addXP(xpGain);
      updateCurrencies();
      syncStateToD1();
    }

export function upgradeCompound() {
      playSFX('click');
      const curLv = state.compoundLevel || 1;
      const upgradeCosts = {
        1: { name: "Tier 2: Surveillance Nest", cash: 1000, scrap: 150, nextLv: 2, unlocks: "Sector 2 (Town East Mall)" },
        2: { name: "Tier 3: Reinforced Bunker", cash: 4000, scrap: 400, nextLv: 3, unlocks: "Sector 3 (Mesquite Rodeo)" },
        3: { name: "Tier 4: Syndicate Citadel", cash: 10000, scrap: 1000, nextLv: 4, unlocks: "Sector 4 (City Hall Coup)" }
      };

      const cfg = upgradeCosts[curLv];
      if (!cfg) {
        playActionSequence({
          title: "MAXIMUM FORTRESS LEVEL",
          icon: "🏰",
          detail: "Room 114 Operations Center is at maximum fortification tier (Tier 4 Citadel).",
          rewardText: "Syndicate Dominion Established",
          shots: 0
        });
        return;
      }

      if (state.cash < cfg.cash || state.scrap < cfg.scrap) {
        playActionSequence({
          title: "INSUFFICIENT RESOURCES FOR UPGRADE",
          icon: "❌",
          detail: `Requires $${cfg.cash.toLocaleString()} Cash and ${cfg.scrap.toLocaleString()} Scrap. (You have: $${state.cash.toLocaleString()} Cash, ${state.scrap.toLocaleString()} Scrap)`,
          rewardText: "Complete missions, salvage scrap, or launder funds",
          shots: 0
        });
        return;
      }

      state.cash -= cfg.cash;
      state.scrap -= cfg.scrap;
      state.compoundLevel = cfg.nextLv;

      if (cfg.nextLv === 2 && state.territories.s2) state.territories.s2.unlocked = true;
      if (cfg.nextLv === 3 && state.territories.s3) state.territories.s3.unlocked = true;
      if (cfg.nextLv === 4 && state.territories.s4) state.territories.s4.unlocked = true;

      updateCompoundUI();
      updateCurrencies();
      updateMissionProgress('upgrade_compound', cfg.nextLv, true);
      syncStateToD1();

      playActionSequence({
        title: `HQ FORTIFIED: ${cfg.name.toUpperCase()}`,
        icon: "🏢",
        detail: `Room 114 upgraded! Unlocked: ${cfg.unlocks}`,
        rewardText: "+500 XP • Syndicate Territory Expanded",
        shots: 2
      });
      addXP(500);
    }

export function updateCompoundUI() {
      const curLv = state.compoundLevel || 1;
      const tierEl = document.getElementById('compound-tier-display');
      const btnEl = document.getElementById('btn-upgrade-compound');
      const hintEl = document.getElementById('compound-cost-hint');

      const tierNames = {
        1: "TIER 1: MOTEL STARTER PAD (BASE LEVEL 1)",
        2: "TIER 2: SURVEILLANCE NEST (SECTOR 2 UNLOCKED)",
        3: "TIER 3: REINFORCED BUNKER (SECTOR 3 UNLOCKED)",
        4: "TIER 4: SYNDICATE CITADEL (MAX TIER)"
      };

      if (tierEl) tierEl.innerText = tierNames[curLv] || `TIER ${curLv} COMPOUND`;

      if (btnEl && hintEl) {
        if (curLv === 1) {
          btnEl.innerText = "⚡ UPGRADE HQ (LV. 2)";
          btnEl.disabled = false;
          hintEl.innerText = "Cost: $1,000 Cash • 150 Scrap";
        } else if (curLv === 2) {
          btnEl.innerText = "⚡ UPGRADE HQ (LV. 3)";
          btnEl.disabled = false;
          hintEl.innerText = "Cost: $4,000 Cash • 400 Scrap";
        } else if (curLv === 3) {
          btnEl.innerText = "⚡ UPGRADE HQ (LV. 4)";
          btnEl.disabled = false;
          hintEl.innerText = "Cost: $10,000 Cash • 1,000 Scrap";
        } else {
          btnEl.innerText = "⭐ MAX TIER FORTRESS";
          btnEl.disabled = true;
          hintEl.innerText = "Citadel fully fortified";
        }
      }

      updateSectorLockVisuals();
    }

export function updateSectorLockVisuals() {
      const lv = state.compoundLevel || 1;
      ['s2', 's3', 's4'].forEach(sKey => {
        const node = document.getElementById('node-' + sKey);
        const reqLv = sKey === 's2' ? 2 : sKey === 's3' ? 3 : 4;
        const isLocked = lv < reqLv;
        if (node) {
          node.classList.toggle('node-locked', isLocked);
          let badge = node.querySelector('.node-lock-badge');
          if (isLocked) {
            if (!badge) {
              badge = document.createElement('div');
              badge.className = 'node-lock-badge';
              badge.innerText = `🔒 LV. ${reqLv}`;
              node.appendChild(badge);
            }
          } else if (badge) {
            badge.remove();
          }
        }
      });
    }

export function bribePoliceHeat() {
      const cost = 75;
      if (state.cred < cost) {
        playActionSequence({
          title: "DIAMONDS REQUIRED",
          icon: "💎",
          detail: `Need ${cost} Diamonds (💎) to bribe county police dispatch.`,
          rewardText: "Get Diamonds via Venmo @ArkitechEVL",
          shots: 0
        });
        openVenmoStoreModal();
        return;
      }

      state.cred -= cost;
      state.policeHeat = 0;
      updateCurrencies();
      update4XTelemetry();
      playSFX('cash');
      syncStateToD1();

      const scannerEl = document.getElementById('scanner-text');
      if (scannerEl) scannerEl.innerText = `MESQUITE PD [DISPATCH]: Stand down all units. Internal affairs code 10-99 filed. US-80 and I-635 corridors all clear.`;

      log(`[HEAT BRIBE]: Paid 💎 ${cost} Diamonds. Police heat wiped to 0 Stars! Full immunity active.`);
      playActionSequence({
        title: "POLICE HEAT WIPED",
        icon: "🚨",
        detail: "County dispatch records erased! All squad cars and radar checkpoints stood down.",
        rewardText: "POLICE HEAT: 0 STARS • FULL IMMUNITY ACTIVE (-75 💎)",
        shots: 0
      });
    }

export function fortifyIronGarrison() {
      const cost = 100;
      if (state.cred < cost) {
        playActionSequence({
          title: "DIAMONDS REQUIRED",
          icon: "💎",
          detail: `Need ${cost} Diamonds (💎) to contract the Iron Garrison mercenaries.`,
          rewardText: "Get Diamonds via Venmo @ArkitechEVL",
          shots: 0
        });
        openVenmoStoreModal();
        return;
      }

      state.cred -= cost;
      const targetSector = activeSectorKey || 's1';
      let t = state.territories[targetSector];
      if (t) {
        t.troops = (t.troops || 0) + 35;
        t.defenseLevel = (t.defenseLevel || 2) + 3;
        t.control = Math.min(100, (t.control || 0) + 25);
      }

      updateCurrencies();
      update4XTelemetry();
      if (activeSectorKey) selectSector(activeSectorKey);
      playSFX('vault');
      syncStateToD1();

      const secName = sectorData[targetSector] ? sectorData[targetSector].title : targetSector;
      log(`[GARRISON]: Deployed Iron Garrison to ${secName} (+35 Troops, +3 Def Lv, +25% Control).`);
      playActionSequence({
        title: "IRON GARRISON DEPLOYED",
        icon: "🏰",
        detail: `Heavily armed mercenary unit fortified ${secName}!`,
        rewardText: "+35 Troops • +3 Defense Level • +25% Control (-100 💎)",
        shots: 2
      });
    }

